import { NextSeo, ProductJsonLd } from 'next-seo'
import React from 'react'
import { jsonLdScriptProps } from 'react-schemaorg'
import { Product } from 'schema-dts'
import { getProfileIndexFromLanguage } from '../getProfile'
import { languageAlternate } from './categoryMetaTags'
import { getCanonicalUrl } from './getCanonicalUrl'
interface ProductMetaTagsProps {
  data: Record<string, any>
  locale: string
  locales: string[]
  shopSettingsData: Record<string, any>
  productSlug: string
  defaultLocale: string
  initialI18nStore: Record<string, any>
}

export const productMetaTags = ({
  data,
  locale,
  locales,
  shopSettingsData,
  productSlug,
  defaultLocale,
  initialI18nStore,
}: ProductMetaTagsProps) => {
  if (data.single_product) {
    // --- Single product
    return singleProductMetaTags({
      data,
      locale,
      locales,
      shopSettingsData,
      productSlug,
      defaultLocale,
      initialI18nStore,
    })
  } else if (data.advanced_product) {
    // --- Advanced product
    // return null
    return advancedProductMetaTags({
      data,
      locale,
      locales,
      shopSettingsData,
      productSlug,
      defaultLocale,
      initialI18nStore,
    })
  } else {
    // --- Unhandled product type
    return null
  }
}

export const singleProductMetaTags = ({
  data,
  locale,
  locales,
  shopSettingsData,
  productSlug,
  defaultLocale,
  initialI18nStore,
}: ProductMetaTagsProps) => {
  //* ======================================================================
  //* ================ PROFILE INDEXES MATCHING LANGUAGE ===================
  //* ======================================================================
  //!!!! if something breaks check this out
  // const productProfileIndex = getProfileIndexFromLanguage(
  //   data.findUniqueMetaInformation.ProductProfile,
  //   locale
  // );
  const colorProfileIndex = getProfileIndexFromLanguage(
    data.single_product.main_color?.color_profiles,
    locale,
  )
  const styleProfileIndex = getProfileIndexFromLanguage(
    data.style?.style_profiles,
    locale,
  )
  const collectionProfileIndex = getProfileIndexFromLanguage(
    data.collection?.collection_profiles,
    locale,
  )
  const shopProfileIndex = getProfileIndexFromLanguage(
    shopSettingsData.data.findFirstShopSetting?.shop_profiles,
    locale,
  )
  const primaryCategoryProfileIndex = getProfileIndexFromLanguage(
    data.primary_category?.category_profiles,
    locale,
  )

  //* ============================================================
  //* ====================== PROFILES ============================
  //* ============================================================
  //!if the index passed to the profiles array is null, the profile will be undefined
  const productProfile = data.single_product?.product_profile
  const colorProfile = data.single_product.main_color?.color_profiles[colorProfileIndex]
  //TODO: IMPORTANT fix this to have the primary category
  const primaryCategoryProfile =
    data.primary_category?.category_profiles[primaryCategoryProfileIndex]
  const styleProfile =
    data.style?.style_profiles[styleProfileIndex]
  const collectionProfile =
    data.collection?.collection_profiles[collectionProfileIndex]
  const shopProfile =
    shopSettingsData.data.findFirstShopSetting?.shop_profiles[shopProfileIndex]

  // ========================
  // ====== CONSTANTS =======
  // ========================
  const shopSettings = shopSettingsData.data.findFirstShopSetting
  const shopName = shopProfile?.name ?? 'Furnisystems'
  const images = data.single_product.images
    .slice()
    .sort((a, b) => (a.display_order > b.display_order ? 1 : -1))
  //TODO: change the fallback to the shop settings fallback image once added to the schema
  const image = data?.single_product.images[0]?.src ?? '/fallbacks/imageNotFound.png'

  const permalink = productProfile.meta_information.permalink
  const price = data.single_product.price
  const productName = productProfile?.name
  const colorName = colorProfile?.name ?? ''
  const material = productProfile?.material ?? ''
  const currency = shopSettings?.currency ?? 'EUR'

  //* ========================================
  //* ============canonical url===============
  //* ========================================
  //here we should get the url we want to refer to
  //if the current locale is /lv we want google to refer to the .lv domain
  //we do so by passing the locale to the "getCanonicalUrl" (which should be kept up to date with the Language schema enum)
  const domainToRefer = getCanonicalUrl(locale, shopSettings?.enabled_languages)
  const currentDomain = getCanonicalUrl(
    defaultLocale,
    shopSettings?.enabled_languages,
  )
  const canonicalUrl = `${domainToRefer}/${productSlug}/${permalink}`
  const url = `${currentDomain}/${locale}/${productSlug}/${permalink}`

  //TODO: add primary category in the conditional chain
  //* ========================================================
  //* ================== TITLE CONSTRUCTOR ===================
  //* ========================================================
  //*The fallback title will be formatted like so:
  //* <product name> | <color?> | <primary category?> | <style?> | <collection?> | <shop name>
  //SOME PARAMS MIGHT RESULT IN UNDEFINED DUE TO THE PROFILE NOT BEING FOUND
  //IT'S NOT A PROBLEM, THE CATEGORY WON'T BE DISPLAYED IF IT DOESN'T HAVE A PROFILE
  const titleParams = {
    firstParam: productName,
    secondParam: colorProfile
      ? colorProfile.name
      : primaryCategoryProfile?.name
        ? primaryCategoryProfile?.name
        : styleProfile
          ? styleProfile.name
          : collectionProfile
            ? collectionProfile.name
            : '',
    thirdParam: shopName,
  }

  //* ==============================================================
  //* ================== DESCRIPTION CONSTRUCTOR ===================
  //* ==============================================================
  const descriptionParams = {
    firstParam: productName,
    secondParam: styleProfile
      ? styleProfile.name
      : collectionProfile
        ? collectionProfile.name
        : shopName,
    thirdParam: primaryCategoryProfile?.name ?? null,
  }
  let descriptionTemplate = ''

  //* =================================
  //* =================================
  //* =========== TEMPLATES (SINGLE PRODUCT)  ===========
  //* =================================
  // TODO maybe add colors / product attributes to the description
  switch (locale) {
    case 'lt':
      descriptionTemplate = `Pirkite ${descriptionParams.firstParam} iš ${descriptionParams.secondParam
        } ${descriptionParams.thirdParam && `- ${descriptionParams.thirdParam}`} `
      break
    case 'et':
      descriptionTemplate = `Osta ${descriptionParams.firstParam} kohta ${descriptionParams.secondParam
        } ${descriptionParams.thirdParam && `- ${descriptionParams.thirdParam}`}`
      break
    case 'lv':
      descriptionTemplate = `Pirkt ${descriptionParams.firstParam} no ${descriptionParams.secondParam
        } ${descriptionParams.thirdParam && `- ${descriptionParams.thirdParam}`}`
      break
    case 'en':
      descriptionTemplate = `Buy ${descriptionParams.firstParam} from ${descriptionParams.secondParam
        } ${descriptionParams.thirdParam && `- ${descriptionParams.thirdParam}`}`
      break
    case 'ru':
      descriptionTemplate = `Купить ${descriptionParams.firstParam} из ${descriptionParams.secondParam
        } ${descriptionParams.thirdParam && `- ${descriptionParams.thirdParam}`}`
      break
    case 'de':
      descriptionTemplate = `Kauf es ${descriptionParams.firstParam} aus ${descriptionParams.secondParam
        } ${descriptionParams.thirdParam && `- ${descriptionParams.thirdParam}`}`
      break
    case 'dk':
      descriptionTemplate = `Købe det ${descriptionParams.firstParam} fra ${descriptionParams.secondParam
        } ${descriptionParams.thirdParam && `- ${descriptionParams.thirdParam}`}`
      break
    case 'es':
      descriptionTemplate = `Cómpralo ${descriptionParams.firstParam} de ${descriptionParams.secondParam
        } ${descriptionParams.thirdParam && `- ${descriptionParams.thirdParam}`}`
      break
    case 'fr':
      descriptionTemplate = `Achète-le ${descriptionParams.firstParam} depuis ${descriptionParams.secondParam
        } ${descriptionParams.thirdParam && `- ${descriptionParams.thirdParam}`}`
      break
    case 'it':
      descriptionTemplate = `Compralo ${descriptionParams.firstParam} da ${descriptionParams.secondParam
        } ${descriptionParams.thirdParam && `- ${descriptionParams.thirdParam}`}`
      break
    case 'nl':
      descriptionTemplate = `Koop het ${descriptionParams.firstParam} van ${descriptionParams.secondParam
        } ${descriptionParams.thirdParam && `- ${descriptionParams.thirdParam}`}`
      break
    case 'pl':
      descriptionTemplate = `Kup to ${descriptionParams.firstParam} z ${descriptionParams.secondParam
        } ${descriptionParams.thirdParam && `- ${descriptionParams.thirdParam}`}`
      break
    case 'pt':
      descriptionTemplate = `Compre ${descriptionParams.firstParam} de ${descriptionParams.secondParam
        } ${descriptionParams.thirdParam && `- ${descriptionParams.thirdParam}`}`
      break
    default:
      console.log(
        'GO ADD THE NEW LANGUAGE IN THE METATAGS UTILITY! We hit the default case in the creation of the product template for meta_description. This could mean that you forgot to add a new language to the switch',
      )
      descriptionTemplate = `Buy ${descriptionParams.firstParam} from ${descriptionParams.secondParam
        } ${descriptionParams.thirdParam && `- ${descriptionParams.thirdParam}`}`
      break
  }

  // Each variation of the page should include a set of <link> elements in the <head> element
  // one link for each page variant including itself. The set of links is identical for every version of the page
  // - (https://developers.google.com/search/docs/advanced/crawling/localized-versions)
  let languageAlternates: languageAlternate[] = []

  for (let locale of locales) {
    for (let key in initialI18nStore) {
      if (key === locale) {
        languageAlternates.push({
          hrefLang: locale,
          href: `${currentDomain}/${locale}/${initialI18nStore[locale].common['product-slug']}`,
        })
      }
    }
  }

  // ---- EACH Product will have a primary category or a categories list
  // --- This list will include schema_href variable which is a link to a wikiData entity page.
  // --- to be used for the sameAS property
  // We use primary category, if it doesnt exist we fall back to one of the categories
  let productSameAs = data.primary_category
    ? data.primary_category
      .schema_href
    : data.categories.length > 0
      ? data.categories[0].schema_href
      : null
  // Edit saving bug in DB... (some reason urls are saved without https...)
  if (productSameAs) {
    productSameAs = productSameAs.replace('//www', 'https://www')
  }

  //* ================================================
  //* ============== END OF PIPELINE =================
  //* ================================================
  const title =
    productProfile.meta_information.meta_title === '' ||
      productProfile.meta_information.meta_title === null
      ? `${titleParams.firstParam} | ${titleParams.secondParam} | ${titleParams.thirdParam}`
      : productProfile.meta_information.meta_title
  let description =
    productProfile.meta_information.meta_description !== '' ||
      productProfile.meta_information.meta_description === null
      ? productProfile.meta_information.meta_description
      : productProfile?.description && productProfile?.description !== ''
        ? productProfile?.description
        : productProfile?.short_description &&
          productProfile?.short_description !== ''
          ? productProfile?.short_description
          : descriptionTemplate && descriptionTemplate !== '' && descriptionTemplate

  return (
    <>
      <NextSeo
        title={title}
        description={description}
        canonical={canonicalUrl}
        additionalMetaTags={[
          {
            property: 'product:price:amount',
            content: price,
          },
          {
            property: 'product:price:currency',
            content: 'EUR',
          },
        ]}
        languageAlternates={languageAlternates}
        openGraph={{
          url,
          title,
          description,
          images: [{ url: image }],
          site_name: shopName,
          type: 'product.group',
        }}
        twitter={{
          handle: '@furnibay',
          site: '@furnibay',
          cardType: 'summary_large_image',
        }}
      />
      {/* //TODO: add the commented out props when website is updated to work with manufacturers and has reviews */}
      <ProductJsonLd
        productName={productName}
        images={[...images.map((image) => image.src)]}
        description={description}
        // brand="ACME"
        color={colorName}
        // manufacturerName="Gary Meehan"
        // manufacturerLogo="https://www.example.com/photos/logo.jpg"
        material={material}
        disambiguatingDescription={description}
        // reviews={[
        //   {
        //     author: {
        //       type: "Person",
        //       name: "Jim",
        //     },
        //     datePublished: "2017-01-06T03:37:40Z",
        //     reviewBody:
        //       "This is my favorite product yet! Thanks Nate for the example products and reviews.",
        //     name: "So awesome!!!",
        //     reviewRating: {
        //       bestRating: "5",
        //       ratingValue: "5",
        //       worstRating: "1",
        //     },
        //     publisher: {
        //       type: "Organization",
        //       name: "TwoVit",
        //     },
        //   },
        // ]}
        // aggregateRating={{
        //   ratingValue: "4.4",
        //   reviewCount: "89",
        // }}
        offers={[
          {
            price: price,
            priceCurrency: currency,
            itemCondition: 'https://schema.org/NewCondition',
            availability: 'https://schema.org/InStock',
            url: url,
            seller: {
              name: shopName,
            },
          },
        ]}
      // mpn="925872"
      />

      <script
        {...jsonLdScriptProps<Product>({
          '@context': 'https://schema.org',
          '@type': 'Product',
          image: image,
          url: url,
          name: productName,
          sameAs: productSameAs, // To reference the itemType
          offers: {
            '@type': 'Offer',
            price: price,
            priceCurrency: currency,
          },
        })}
      />
    </>
  )
}

export const advancedProductMetaTags = ({
  data,
  locale,
  locales,
  shopSettingsData,
  productSlug,
  defaultLocale,
  initialI18nStore,
}: ProductMetaTagsProps) => {
  //* ======================================================================
  //* ================ PROFILE INDEXES MATCHING LANGUAGE ===================
  //* ======================================================================
  //!!!! if something breaks check this out
  // const productProfileIndex = getProfileIndexFromLanguage(
  //   data.findUniqueMetaInformation.ProductProfile,
  //   locale
  // );

  const styleProfileIndex = getProfileIndexFromLanguage(
    data.style?.style_profiles,
    locale,
  )
  const collectionProfileIndex = getProfileIndexFromLanguage(
    data.collection?.collection_profiles,
    locale,
  )
  const shopProfileIndex = getProfileIndexFromLanguage(
    shopSettingsData.data.findFirstShopSetting?.shop_profiles,
    locale,
  )
  const primaryCategoryProfileIndex = getProfileIndexFromLanguage(
    data.primary_category?.category_profiles,
    locale,
  )

  //* ============================================================
  //* ====================== PROFILES ============================
  //* ============================================================
  //!if the index passed to the profiles array is null, the profile will be undefined
  const productProfile = data.advanced_product?.advanced_product_profile
  //TODO: IMPORTANT fix this to have the primary category
  const primaryCategoryProfile =
    data.primary_category?.category_profiles[
    primaryCategoryProfileIndex
    ]
  const styleProfile =
    data.style?.style_profiles[styleProfileIndex]
  const collectionProfile =
    data.collection?.collection_profiles[collectionProfileIndex]
  const shopProfile =
    shopSettingsData.data.findFirstShopSetting?.shop_profiles[shopProfileIndex]

  // ========================
  // ====== CONSTANTS =======
  // ========================
  const shopSettings = shopSettingsData.data.findFirstShopSetting
  const shopName = shopProfile?.name ?? 'Furnisystems'
  const images = data.advanced_product.images
    .slice()
    .sort((a, b) => (a.display_order > b.display_order ? 1 : -1))
  //TODO: change the fallback to the shop settings fallback image once added to the schema
  const image = data?.advanced_product.images[0]?.src ??
    '/fallbacks/imageNotFound.png'

  const permalink = productProfile.meta_information.permalink
  const price = data.advanced_product.price_from ?? null
  const productName = productProfile.name
  const material = productProfile.material ?? ''
  const currency = shopSettings?.currency ?? 'EUR'

  //* ========================================
  //* ============canonical url===============
  //* ========================================
  //here we should get the url we want to refer to
  //if the current locale is /lv we want google to refer to the .lv domain
  //we do so by passing the locale to the "getCanonicalUrl" (which should be kept up to date with the Language schema enum)
  const domainToRefer = getCanonicalUrl(locale, shopSettings?.enabled_languages)
  const currentDomain = getCanonicalUrl(
    defaultLocale,
    shopSettings?.enabled_languages,
  )
  const canonicalUrl = `${domainToRefer}/${productSlug}/${permalink}`
  const url = `${currentDomain}/${locale}/${productSlug}/${permalink}`

  //TODO: add primary category in the conditional chain
  //* ========================================================
  //* ================== TITLE CONSTRUCTOR ===================
  //* ========================================================
  //*The fallback title will be formatted like so:
  //* <product name> | <color?> | <primary category?> | <style?> | <collection?> | <shop name>
  //SOME PARAMS MIGHT RESULT IN UNDEFINED DUE TO THE PROFILE NOT BEING FOUND
  //IT'S NOT A PROBLEM, THE CATEGORY WON'T BE DISPLAYED IF IT DOESN'T HAVE A PROFILE
  const titleParams = {
    firstParam: productName,
    secondParam: primaryCategoryProfile?.name
      ? primaryCategoryProfile?.name
      : styleProfile
        ? styleProfile.name
        : collectionProfile
          ? collectionProfile.name
          : '',
    thirdParam: shopName,
  }

  //* ==============================================================
  //* ================== DESCRIPTION CONSTRUCTOR ===================
  //* ==============================================================
  const descriptionParams = {
    firstParam: productName,
    secondParam: styleProfile
      ? styleProfile.name
      : collectionProfile
        ? collectionProfile.name
        : shopName,
    thirdParam: primaryCategoryProfile?.name ?? null,
  }
  let descriptionTemplate = ''

  //* =================================
  //* =========== TEMPLATES (Advanced product) ===========
  //* =================================
  // TODO maybe add colors / product attributes to the description
  switch (locale) {
    case 'lt':
      descriptionTemplate = `Pirkite ${descriptionParams.firstParam} iš ${descriptionParams.secondParam
        } ${descriptionParams.thirdParam && `- ${descriptionParams.thirdParam}`} `
      break
    case 'et':
      descriptionTemplate = `Osta ${descriptionParams.firstParam} kohta ${descriptionParams.secondParam
        } ${descriptionParams.thirdParam && `- ${descriptionParams.thirdParam}`}`
      break
    case 'lv':
      descriptionTemplate = `Pirkt ${descriptionParams.firstParam} no ${descriptionParams.secondParam
        } ${descriptionParams.thirdParam && `- ${descriptionParams.thirdParam}`}`
      break
    case 'en':
      descriptionTemplate = `Buy ${descriptionParams.firstParam} from ${descriptionParams.secondParam
        } ${descriptionParams.thirdParam && `- ${descriptionParams.thirdParam}`}`
      break
    case 'ru':
      descriptionTemplate = `Купить ${descriptionParams.firstParam} из ${descriptionParams.secondParam
        } ${descriptionParams.thirdParam && `- ${descriptionParams.thirdParam}`}`
      break
    case 'de':
      descriptionTemplate = `Kauf es ${descriptionParams.firstParam} aus ${descriptionParams.secondParam
        } ${descriptionParams.thirdParam && `- ${descriptionParams.thirdParam}`}`
      break
    case 'dk':
      descriptionTemplate = `Købe det ${descriptionParams.firstParam} fra ${descriptionParams.secondParam
        } ${descriptionParams.thirdParam && `- ${descriptionParams.thirdParam}`}`
      break
    case 'es':
      descriptionTemplate = `Cómpralo ${descriptionParams.firstParam} de ${descriptionParams.secondParam
        } ${descriptionParams.thirdParam && `- ${descriptionParams.thirdParam}`}`
      break
    case 'fr':
      descriptionTemplate = `Achète-le ${descriptionParams.firstParam} depuis ${descriptionParams.secondParam
        } ${descriptionParams.thirdParam && `- ${descriptionParams.thirdParam}`}`
      break
    case 'it':
      descriptionTemplate = `Compralo ${descriptionParams.firstParam} da ${descriptionParams.secondParam
        } ${descriptionParams.thirdParam && `- ${descriptionParams.thirdParam}`}`
      break
    case 'nl':
      descriptionTemplate = `Koop het ${descriptionParams.firstParam} van ${descriptionParams.secondParam
        } ${descriptionParams.thirdParam && `- ${descriptionParams.thirdParam}`}`
      break
    case 'pl':
      descriptionTemplate = `Kup to ${descriptionParams.firstParam} z ${descriptionParams.secondParam
        } ${descriptionParams.thirdParam && `- ${descriptionParams.thirdParam}`}`
      break
    case 'pt':
      descriptionTemplate = `Compre ${descriptionParams.firstParam} de ${descriptionParams.secondParam
        } ${descriptionParams.thirdParam && `- ${descriptionParams.thirdParam}`}`
      break
    default:
      console.log(
        'GO ADD THE NEW LANGUAGE IN THE METATAGS UTILITY! We hit the default case in the creation of the product template for meta_description. This could mean that you forgot to add a new language to the switch',
      )
      descriptionTemplate = `Buy ${descriptionParams.firstParam} from ${descriptionParams.secondParam
        } ${descriptionParams.thirdParam && `- ${descriptionParams.thirdParam}`}`
      break
  }

  // Each variation of the page should include a set of <link> elements in the <head> element
  // one link for each page variant including itself. The set of links is identical for every version of the page
  // - (https://developers.google.com/search/docs/advanced/crawling/localized-versions)
  let languageAlternates: languageAlternate[] = []

  for (let locale of locales) {
    for (let key in initialI18nStore) {
      if (key === locale) {
        languageAlternates.push({
          hrefLang: locale,
          href: `${currentDomain}/${locale}/${initialI18nStore[locale].common['product-slug']}`,
        })
      }
    }
  }

  // ---- EACH Product will have a primary category or a categories list
  // --- This list will include schema_href variable which is a link to a wikiData entity page.
  // --- to be used for the sameAS property
  // We use primary category, if it doesnt exist we fall back to one of the categories
  let productSameAs = data.primary_category
    ? data.primary_category
      .schema_href
    : data.categories.length > 0
      ? data.categories[0]
        .schema_href
      : null
  // Edit saving bug in DB... (some reason urls are saved without https...)
  if (productSameAs) {
    productSameAs = productSameAs.replace('//www', 'https://www')
  }

  //* ================================================
  //* ============== END OF PIPELINE =================
  //* ================================================
  const title =
    productProfile.meta_information.meta_title === '' ||
      productProfile.meta_information.meta_title === null
      ? `${titleParams.firstParam} | ${titleParams.secondParam} | ${titleParams.thirdParam}`
      : productProfile.meta_information.meta_title
  let description =
    productProfile.meta_information.meta_description !== '' ||
      productProfile.meta_information.meta_description === null
      ? productProfile.meta_information.meta_description
      : productProfile?.description && productProfile?.description !== ''
        ? productProfile?.description
        : productProfile?.short_description &&
          productProfile?.short_description !== ''
          ? productProfile?.short_description
          : descriptionTemplate && descriptionTemplate !== '' && descriptionTemplate

  return (
    <>
      <NextSeo
        title={title}
        description={description}
        canonical={canonicalUrl}
        additionalMetaTags={[
          {
            property: 'product:price:amount',
            content: price,
          },
          {
            property: 'product:price:currency',
            content: 'EUR',
          },
        ]}
        languageAlternates={languageAlternates}
        openGraph={{
          url,
          title,
          description,
          images: [{ url: image }],
          site_name: shopName,
          type: 'product.group',
        }}
      // twitter={{
      //   handle: '@furnibay',
      //   site: '@furnibay',
      //   cardType: 'summary_large_image',
      // }}
      />
      {/* //TODO: add the commented out props when website is updated to work with manufacturers and has reviews */}
      <ProductJsonLd
        productName={productName}
        images={[...images.map((image) => image.src)]}
        description={description}
        // brand="ACME"
        // color={colorName}
        // manufacturerName="Gary Meehan"
        // manufacturerLogo="https://www.example.com/photos/logo.jpg"
        material={material}
        disambiguatingDescription={description}
        // reviews={[
        //   {
        //     author: {
        //       type: "Person",
        //       name: "Jim",
        //     },
        //     datePublished: "2017-01-06T03:37:40Z",
        //     reviewBody:
        //       "This is my favorite product yet! Thanks Nate for the example products and reviews.",
        //     name: "So awesome!!!",
        //     reviewRating: {
        //       bestRating: "5",
        //       ratingValue: "5",
        //       worstRating: "1",
        //     },
        //     publisher: {
        //       type: "Organization",
        //       name: "TwoVit",
        //     },
        //   },
        // ]}
        // aggregateRating={{
        //   ratingValue: "4.4",
        //   reviewCount: "89",
        // }}
        offers={[
          {
            price: price,
            priceCurrency: currency,
            itemCondition: 'https://schema.org/NewCondition',
            availability: 'https://schema.org/InStock',
            url: url,
            seller: {
              name: shopName,
            },
          },
        ]}
      // mpn="925872"
      />

      <script
        {...jsonLdScriptProps<Product>({
          '@context': 'https://schema.org',
          '@type': 'Product',
          image: image,
          url: url,
          name: productName,
          sameAs: productSameAs, // To reference the itemType
          offers: {
            '@type': 'Offer',
            price: price,
            priceCurrency: currency,
          },
        })}
      />
    </>
  )
}
